import React from 'react';
import { Heart, LogOut, ChevronRight, Info, Mail, Crown, RefreshCw, BookOpen, User, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const EmailVerifyBanner = () => {
  const [status, setStatus] = React.useState('idle');
  const resend = async () => {
    setStatus('sending');
    try {
      await api.request('/api/auth/send-verification', { method: 'POST' });
      setStatus('sent');
    } catch (e) {
      setStatus('idle');
    }
  };
  return (
    <div className="mb-6 px-4 py-3 rounded-2xl bg-[#FFFDF6] border border-[#F3EEE5] flex items-center justify-between" data-testid="email-verify-banner">
      <p className="text-[12px] text-gray-500 font-light pr-3">
        {status === 'sent' ? 'Email de confirmation envoyé — pense à vérifier tes spams.' : 'Ton adresse email n\'est pas encore confirmée.'}
      </p>
      {status !== 'sent' && (
        <button
          onClick={resend}
          disabled={status === 'sending'}
          className="text-[12px] text-[#F79AAE] whitespace-nowrap disabled:opacity-50"
          data-testid="resend-verification-btn"
        >
          {status === 'sending' ? 'Envoi…' : "Renvoyer l'email"}
        </button>
      )}
    </div>
  );
};

const ProfilHeader = ({ firstName, action, onSettings }) => (
  <div className="relative mb-5">
    {onSettings && (
      <button
        onClick={onSettings}
        aria-label="Paramètres du compte"
        className="absolute top-0 right-0 z-10 p-2 text-gray-300 hover:text-[#F79AAE] transition-colors"
        data-testid="profil-settings-btn"
      >
        <Settings size={18} strokeWidth={1.5} />
      </button>
    )}
    <div className="absolute -top-2 right-4 w-40 h-32 pointer-events-none">
      <div className="absolute inset-8 rounded-full" style={{ background: 'radial-gradient(circle, rgba(247,154,174,0.14), rgba(247,154,174,0) 70%)' }} />
      <img src="/dandelion-salam-v2.png" alt="" className="w-full h-full object-contain" />
      {/* 3 aigrettes détachées — flottement très doux, décalé, désactivé si prefers-reduced-motion */}
      {[
        { left: '58%', top: '18%', delay: '0s', dur: '11s', size: 13 },
        { left: '70%', top: '10%', delay: '3.5s', dur: '13s', size: 11 },
        { left: '48%', top: '28%', delay: '7s', dur: '12s', size: 10 },
      ].map((a, i) => (
        <svg
          key={i}
          className="absolute pissenlit-aigrette"
          style={{ left: a.left, top: a.top, animationDelay: a.delay, animationDuration: a.dur }}
          width={a.size}
          height={a.size}
          viewBox="0 0 14 14"
          fill="none"
          stroke="#E8A9B8"
          strokeWidth="0.9"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M7 13 L7 6" />
          <path d="M7 6 L3 1.5 M7 6 L5 1 M7 6 L7 0.5 M7 6 L9 1 M7 6 L11 1.5" />
        </svg>
      ))}
      <style>{`
        @keyframes aigretteFlotte {
          0%   { transform: translate(0, 0) rotate(0deg); opacity: 0; }
          12%  { opacity: 0.85; }
          55%  { transform: translate(7px, -14px) rotate(9deg); opacity: 0.7; }
          88%  { opacity: 0.15; }
          100% { transform: translate(13px, -24px) rotate(14deg); opacity: 0; }
        }
        .pissenlit-aigrette { opacity: 0; animation-name: aigretteFlotte; animation-timing-function: ease-in-out; animation-iteration-count: infinite; will-change: transform, opacity; }
        @media (prefers-reduced-motion: reduce) {
          .pissenlit-aigrette { animation: none; opacity: 0; }
        }
      `}</style>
    </div>
    <div className="pt-1">
      <h1 className="font-script" style={{ fontSize: 32, color: '#1a1a1a' }} data-testid="profil-salam">
        Salam
      </h1>
      {firstName && (
        <p className="font-script" style={{ fontSize: 23, color: '#3a3a3a', marginTop: 1 }} data-testid="profil-firstname">
          {firstName}
        </p>
      )}
      <div className="w-10 h-[2px] bg-[#F79AAE] mt-1" />
      {action}
    </div>
  </div>
);

const Row = ({ icon: Icon, title, subtitle, onClick, badge, premium, testId }) => (
  <button
    onClick={onClick}
    disabled={!onClick}
    className="w-full flex items-center gap-4 px-6 min-h-[62px] hover:bg-[#FAF9F6] transition-colors text-left"
    data-testid={testId}
  >
    <Icon size={19} strokeWidth={1.3} className="text-[#F79AAE] flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className="text-[15px] text-gray-800" style={{ fontWeight: 400 }}>{title}</p>
        {premium && (
          <span
            className="text-[10px] px-2 py-[2px] rounded-full font-light tracking-[0.06em]"
            style={{ background: 'rgba(255, 251, 240, 0.9)', border: '1px solid rgba(197, 168, 128, 0.5)', color: '#8A6D4F' }}
            data-testid={`${testId}-premium-pill`}
          >
            Premium
          </span>
        )}
        {badge && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F3EEE5] text-gray-400">{badge}</span>
        )}
      </div>
      {subtitle && <p className="text-[12px] text-gray-500 font-light mt-0.5">{subtitle}</p>}
    </div>
    {onClick && <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />}
  </button>
);

const PremiumPill = ({ premium, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 text-left rounded-full pl-3 pr-4 py-2 transition-shadow hover:shadow-md"
    style={{ background: 'linear-gradient(135deg, #FDEEF2 0%, #FBE4EA 100%)' }}
    data-testid={premium ? 'mon-abonnement-btn' : 'devenir-premium-btn'}
  >
    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${premium ? 'bg-[#F79AAE] shadow-[0_3px_8px_rgba(247,154,174,0.45)]' : 'bg-[#F79AAE]/25'}`}>
      <Crown size={15} className={premium ? 'text-white' : 'text-[#E8848B]'} strokeWidth={1.8} />
    </div>
    <p className="flex-1 text-[14px] text-gray-800" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}>
      {premium ? 'Mon abonnement Premium' : 'Découvrir Premium'}
    </p>
    <ChevronRight size={15} className="text-[#E8848B] flex-shrink-0" />
  </button>
);

const Divider = () => <div className="mx-6 h-[1px] bg-[#F3EEE5]/60" />;

const CardList = ({ children }) => (
  <div className="bg-[#FFFDF6] border border-[#F3EEE5]/60 rounded-[22px] shadow-[0_1px_6px_rgba(0,0,0,0.025)] overflow-hidden mb-6">
    {children}
  </div>
);

const forceRefresh = async () => {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
    }
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
    try {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith('app_') || k.startsWith('sw_')) localStorage.removeItem(k);
      });
    } catch (e) {}
  } catch (e) {}
  window.location.replace('/?_r=' + Date.now());
};

// Détection d'une nouvelle version : compare le bundle référencé par /index.html
// (réseau, sans cache) avec celui actuellement chargé dans la page.
const useUpdateAvailable = () => {
  const [available, setAvailable] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/index.html?_v=' + Date.now(), { cache: 'no-store' });
        if (!res.ok) return;
        const html = await res.text();
        const m = html.match(/(?:src)="([^"]*\/(?:main|bundle)[^"]*\.js)"/);
        if (!m) return;
        const latest = m[1];
        const loaded = Array.from(document.querySelectorAll('script[src]')).map((s) => s.getAttribute('src'));
        if (!cancelled && loaded.length > 0 && !loaded.includes(latest)) setAvailable(true);
      } catch (e) {}
    })();
    return () => { cancelled = true; };
  }, []);
  return available;
};

const RefreshButton = ({ testId }) => {
  const updateAvailable = useUpdateAvailable();
  if (!updateAvailable) return null;
  return (
    <button
      onClick={forceRefresh}
      className="w-full flex items-center justify-center py-3 text-gray-500 hover:text-[#F79AAE] transition-colors text-sm"
      aria-label="Mettre à jour l'application"
      data-testid={testId}
    >
      <RefreshCw size={14} className="mr-2" />
      Mettre à jour l'application
    </button>
  );
};

const Profil = () => {  const navigate = useNavigate();
  const { isAuthenticated, logout, isSubscribed, isPremium, user } = useAuth();
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const rawFirst = isAuthenticated && user?.name ? user.name.trim().split(/\s+/)[0] : null;
  const firstName = rawFirst ? rawFirst.charAt(0).toUpperCase() + rawFirst.slice(1) : null;

  const handleLogout = () => {
    logout();
    navigate('/menu');
  };

  const universRow = (
    <Row
      icon={Info}
      title="L'univers Spiritu’L"
      onClick={() => navigate('/a-propos')}
      testId="about-link"
    />
  );
  const contactRow = (
    <Row
      icon={Mail}
      title="Aide et contact"
      onClick={() => navigate('/aide')}
      testId="contact-link"
    />
  );

  // ============ 1. NON CONNECTÉ ============
  if (!isAuthenticated) {
    return (
      <div className="lato-screen min-h-screen bg-[#FAF9F6] pb-24">
        <div className="max-w-md mx-auto px-6 py-6">
          <ProfilHeader />

          <div className="text-center py-6 mb-4">
            <div className="w-24 h-24 rounded-full bg-[#F79AAE]/10 mx-auto mb-6 flex items-center justify-center">
              <User size={38} strokeWidth={1.4} className="text-[#F79AAE]" />
            </div>
            <h2 className="text-[17px] text-gray-800 mb-3">
              Accède à ton espace personnel
            </h2>
            <p className="text-[13px] text-gray-500 font-light leading-relaxed mb-7 px-6">
              Connecte-toi pour retrouver ton journal,<br />tes favoris et suivre ta pratique.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="px-10 py-3.5 bg-[#F79AAE] text-white text-sm rounded-full shadow-[0_6px_16px_rgba(247,154,174,0.4)] hover:bg-[#e8848b] transition-colors"
              data-testid="login-redirect"
            >
              Accéder à mon espace
            </button>
          </div>

          <CardList>
            {universRow}
            <Divider />
            {contactRow}
          </CardList>

          <RefreshButton testId="force-refresh-button-logged-out" />
        </div>
        <BottomNav />
      </div>
    );
  }

  const premium = isSubscribed();

  // ============ 2 & 3. CONNECTÉ ============
  return (
    <div className="min-h-screen bg-[#FAF9F6] pb-24">
      <div
        className="max-w-md mx-auto px-6 py-6 flex flex-col"
        style={{ minHeight: 'calc(100dvh - 96px)' }}
      >
        <ProfilHeader firstName={firstName} onSettings={() => setSettingsOpen(true)} />

        {user?.email_verified === false && <EmailVerifyBanner />}

        {/* Ensemble pastille + menu — centré dans l'espace disponible */}
        <div className="flex-1 flex flex-col justify-center" data-testid="profil-main-group">
          <div style={{ marginBottom: 18 }}>
            <PremiumPill
              premium={premium}
              onClick={() => { navigate('/abonnement'); window.scrollTo(0, 0); }}
            />
          </div>
          <CardList>
          <Row
            icon={BookOpen}
            title="Mon journal"
            onClick={() => navigate('/journal')}
            testId="journal-link"
          />
          <Divider />
          <Row
            icon={Heart}
            title="Ma bibliothèque"
            onClick={() => navigate('/favoris')}
            testId="library-link"
          />
          <Divider />
          <Row
            icon={Info}
            title="À propos de Spiritu’L"
            onClick={() => navigate('/a-propos')}
            testId="apropos-link"
          />
          <Divider />
          <Row
            icon={Mail}
            title="Aide & FAQ"
            onClick={() => navigate('/aide')}
            testId="aide-faq-link"
          />
        </CardList>
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2.5 min-h-[52px] rounded-[22px] bg-[#FFF5F7] border border-[#F79AAE]/25 hover:bg-[#FDEBEF] transition-colors"
          data-testid="logout-button"
        >
          <LogOut size={17} strokeWidth={1.6} className="text-[#E8798F] flex-shrink-0" />
          <span className="text-[15px] text-[#E8798F]" style={{ fontWeight: 500 }}>Se déconnecter</span>
        </button>
        </div>

        <div className="text-center" style={{ marginTop: 'auto', paddingTop: 24, marginBottom: -24 }}>
          <p
            className="text-[11px] font-light"
            style={{ color: 'rgba(247,154,174,0.55)' }}
            data-testid="profil-signature"
          >
            Spiritu’L
          </p>
          <button
            onClick={() => { navigate('/cgv'); window.scrollTo(0, 0); }}
            className="w-full text-center text-[11px] text-gray-300 hover:text-[#F79AAE] transition-colors underline underline-offset-2 decoration-gray-200 mt-2"
            data-testid="profil-cgv-link"
          >
            Conditions Générales de Vente
          </button>
        </div>
      </div>

      {/* Feuille — Paramètres du compte */}
      {settingsOpen && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center" data-testid="settings-sheet">
          <div className="absolute inset-0 bg-black/25" onClick={() => setSettingsOpen(false)} />
          <div className="relative w-full max-w-md bg-[#FFFDF6] rounded-t-[26px] px-4 pt-4 pb-10 shadow-[0_-8px_40px_rgba(0,0,0,0.08)]">
            <div className="w-10 h-1 rounded-full bg-[#EDE7DC] mx-auto mb-3" />
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-4 px-5 min-h-[58px] hover:bg-[#FAF9F6] transition-colors text-left"
              data-testid="logout-button-sheet"
            >
              <LogOut size={19} strokeWidth={1.5} className="text-[#F79AAE] flex-shrink-0" />
              <p className="text-[15px] text-black" style={{ fontWeight: 500 }}>Se déconnecter</p>
            </button>
            <RefreshButton testId="force-refresh-button" />
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default Profil;
